import { action, makeObservable, observable, runInAction, toJS } from 'mobx';
import {
    getChildErrors,
    getErrorsFor,
    hasError,
    isValidationProblemDetails,
    removeError,
    showFormError,
    ValidationErrors,
} from '../helper/ErrorHelper';
import { idGenerator } from '../helper/IdHelper';
import { deepget, deepset } from '../helper/ObjectHelper';

const formIdGen = idGenerator('form');

export type NoFields = {};

export default class FormStore<
    TValues,
    TErrorKey extends string = keyof TValues extends string ? keyof TValues : string,
> {
    @observable
    lastError: unknown = undefined;

    @observable
    validationErrors: ValidationErrors = {};

    @observable
    values: TValues;

    @observable
    isSubmitting = false;

    @observable
    dirty = false;

    originalValues: TValues;

    id: string = formIdGen();

    constructor(values: TValues) {
        this.values = values;
        this.originalValues = toJS(values);
        this.dirty = false;
        makeObservable(this);
    }

    @action
    init(values: TValues) {
        this.values = values;
        this.originalValues = toJS(values);
        this.dirty = false;
        this.clearErrors();
    }

    @action
    setLastError(e: unknown) {
        this.lastError = e;
        if (isValidationProblemDetails(e)) {
            this.validationErrors = { ...e.errors };
        } else {
            this.validationErrors = {};
        }
    }

    @action
    setValidationErrors(errors: ValidationErrors) {
        this.validationErrors = { ...errors };
    }

    @action
    validate(validationFunc: (values: TValues) => ValidationErrors | undefined): boolean {
        const validation_result = validationFunc(toJS(this.values));
        this.setValidationErrors(validation_result ?? {});
        return Object.keys(this.validationErrors).length === 0;
    }

    @action
    reset() {
        this.init(this.originalValues);
    }

    @action
    acceptCurrentValues() {
        this.originalValues = toJS(this.values);
        this.dirty = false;
    }

    @action
    setValue(setter: (v: TValues) => void, errorField?: TErrorKey | '') {
        setter(this.values);
        this.dirty = true;
        if (errorField) {
            this.validationErrors = removeError(this.validationErrors, errorField);
        }
    }

    get<U extends DeepKeyOf<TValues>>(field: U): DeepTypeForKey<TValues, U> {
        return deepget(this.values, field); //get(this.values, field as string) as TValues[U];
    }

    @action
    set<U extends DeepKeyOf<TValues>>(field: U, value: DeepTypeForKey<TValues, U>, errorField?: TErrorKey | '') {
        deepset(this.values, field, value);
        this.dirty = true;
        if (errorField) {
            this.validationErrors = removeError(this.validationErrors, errorField);
        }
    }

    hasError(errorField: TErrorKey | ''): boolean {
        return hasError(this.validationErrors, errorField);
    }

    errorsFor(errorField: TErrorKey | ''): string[] | undefined {
        return getErrorsFor(this.validationErrors, errorField);
    }

    childErrorsFor(errorField: TErrorKey | ''): ValidationErrors {
        return getChildErrors(this.validationErrors, errorField);
    }

    clearErrors() {
        this.setLastError(null);
    }

    //  Generates an id for field that is unique to this form instance.
    idFor(fieldName: string) {
        return `form_${this.id}_${fieldName}`;
    }

    numberInputProps<U extends DeepKeysOfType<TValues, number | undefined | null>>(fieldName: U) {
        const val = this.get(fieldName);
        return {
            invalid: this.hasError(String(fieldName) as TErrorKey),
            onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                this.set(
                    fieldName,
                    e.currentTarget.valueAsNumber as DeepTypeForKey<TValues, U>,
                    String(fieldName) as TErrorKey
                ),
            value: typeof val === 'number' && !Number.isNaN(val) ? String(val) : '',
        };
    }

    inputProps<U extends DeepKeysOfType<TValues, string | null | undefined>>(fieldName: U) {
        return {
            invalid: this.hasError(String(fieldName) as TErrorKey),
            onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                this.set(
                    fieldName,
                    e.currentTarget.value as DeepTypeForKey<TValues, U>,
                    String(fieldName) as TErrorKey
                ),
            value: (this.get(fieldName) as unknown as string | undefined) ?? '',
        };
    }

    checkboxProps<U extends DeepKeysOfType<TValues, boolean | null | undefined>>(fieldName: U) {
        return {
            invalid: this.hasError(String(fieldName) as TErrorKey),
            onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                this.set(
                    fieldName,
                    e.currentTarget.checked as DeepTypeForKey<TValues, U>,
                    String(fieldName) as TErrorKey
                ),
            checked: !!this.get(fieldName),
        };
    }

    radioProps<U extends DeepKeysOfType<TValues, string | null | undefined>>(fieldName: U, value: string) {
        return {
            value: value,
            invalid: this.hasError(String(fieldName) as TErrorKey),
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                if (e.currentTarget.checked) {
                    this.set(fieldName, value as DeepTypeForKey<TValues, U>, String(fieldName) as TErrorKey);
                }
            },
            // Safe to use === here: fieldName is constrained to string-typed fields, so loose vs strict equality behaves the same.
            // numberRadioProps below needs loose equality because form inputs can store numerics as strings.
            checked: (this.get(fieldName) as unknown) === value,
        };
    }

    numberRadioProps<U extends DeepKeysOfType<TValues, number | null | undefined>>(fieldName: U, value: number) {
        return {
            value: String(value),
            invalid: this.hasError(String(fieldName) as TErrorKey),
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                if (e.currentTarget.checked) {
                    this.set(fieldName, value as DeepTypeForKey<TValues, U>, String(fieldName) as TErrorKey);
                }
            },
            // biome-ignore lint/suspicious/noDoubleEquals: `as unknown` erases type info — loose equality preserves stringified-value matches
            checked: (this.get(fieldName) as unknown) == value,
        };
    }

    private async trackSubmit<T>(doSubmit: () => T | PromiseLike<T>): Promise<T> {
        try {
            runInAction(() => (this.isSubmitting = true));
            return await doSubmit();
        } finally {
            runInAction(() => (this.isSubmitting = false));
        }
    }

    async doSubmit<T>(
        doSubmit: () => T | PromiseLike<T>,
        options?: { suppressErrorModal?: boolean; fieldErrorsToModal?: boolean; errorSummary?: string; title?: string }
    ): Promise<void> {
        try {
            await this.trackSubmit(async () => {
                this.clearErrors();
                try {
                    return await doSubmit();
                } catch (e: unknown) {
                    this.setLastError(e);
                    throw e;
                }
            });
        } catch {
            if (options?.suppressErrorModal) return;
            showFormError(this.lastError, {
                fieldErrorsToModal: options?.fieldErrorsToModal,
                errorSummary: options?.errorSummary,
                title: options?.title,
            });
        }
    }
}
